import { useEffect, useMemo } from 'react';
import { Box, Button, Icon, InlineStack, Strong, Text, Tooltip } from '@customerio/pluma-components/react';
import { useStore } from '@nanostores/react';
import { DeliveryModeBanner } from '../delivery-mode-banner';
import { GlobalCSSVariable, useThemeContext } from '@/context/theme';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { selectSandboxSendingDomain } from '@/features/workspaces';
import { useResizeObserver } from '@/hooks/browser/use-resize-observer';
import { useDomainsOptional } from '@/services/db/domain/queries';
import { useBuilderSandboxHomeEnabled } from '@/services/auth/feature-flags';
import { authStore } from '@/services/auth/store';
import { generatePath } from '@/services/router/generate-path';
import { isEmptyString } from '@/utils/string';
import * as styles from './index.css';

const BANNER_HEIGHT_PX = 40;
const SANDBOX_DOMAIN_FALLBACK = 'your sandbox domain';

type SandboxModeBannerProps = {
  goLiveHref: string;
  sandboxSendingDomain: string | null | undefined;
};

function getWorkspaceModeBannerHeight(height: number | undefined, isSandboxModeVisible: boolean): string {
  const fallbackHeight = isSandboxModeVisible ? BANNER_HEIGHT_PX : 0;
  return `${height ?? fallbackHeight}px`;
}

function getSandboxSendingDomainText(sandboxSendingDomain: string | null | undefined): string {
  if (isEmptyString(sandboxSendingDomain)) return SANDBOX_DOMAIN_FALLBACK;
  return sandboxSendingDomain;
}

export function WorkspaceModeBanners() {
  const auth = useStore(authStore);
  const workspace = useCurrentWorkspace();
  const themeContext = useThemeContext();
  const isBuilderSandboxHomeEnabled = useBuilderSandboxHomeEnabled();
  const { data: sandboxSendingDomain } = useDomainsOptional({ select: selectSandboxSendingDomain });
  const [bannersRef, { height }] = useResizeObserver();
  const workspaceId = workspace?.id ?? 0;
  const isSandboxModeVisible =
    auth.account?.sandbox === true && isBuilderSandboxHomeEnabled === true && workspaceId > 0;
  const goLiveHref = useMemo(
    () => `${generatePath('/workspaces/:workspaceId/home', { workspaceId })}?step=go-live`,
    [workspaceId],
  );

  useEffect(() => {
    themeContext.setVariable(
      GlobalCSSVariable.WorkspaceDeliveryModeBannerHeight,
      getWorkspaceModeBannerHeight(height, isSandboxModeVisible),
    );

    return () => {
      themeContext.setVariable(GlobalCSSVariable.WorkspaceDeliveryModeBannerHeight, '0px');
    };
  }, [height, isSandboxModeVisible, themeContext]);

  return (
    <div ref={bannersRef}>
      {isSandboxModeVisible ? (
        <>
          <SandboxModeBanner goLiveHref={goLiveHref} sandboxSendingDomain={sandboxSendingDomain} />
          <div className={styles.sandboxFrameBottom} aria-hidden="true" />
        </>
      ) : null}
      <DeliveryModeBanner />
    </div>
  );
}

function SandboxModeBanner({ goLiveHref, sandboxSendingDomain }: SandboxModeBannerProps) {
  const domainText = getSandboxSendingDomainText(sandboxSendingDomain);

  return (
    <Box className={styles.sandboxModeBanner} aria-label="Sandbox mode" role="region">
      <InlineStack alignBlock="center" gap="050" className={styles.sandboxModeBannerLabel}>
        <Text as="span" size="s" weight="semibold" color="inherit">
          Sandbox
        </Text>
        <Tooltip content="Sandbox sends only to test recipients." isCustom={true}>
          {(context) => (
            <span ref={context.reference.setReference} {...context.reference.getReferenceProps()}>
              <Icon name="info" size="xs" />
            </span>
          )}
        </Tooltip>
      </InlineStack>

      <Text as="span" size="s" className={styles.sandboxModeBannerText}>
        You&apos;re testing in your sandbox &mdash; sending from{' '}
        <Strong className={styles.sandboxModeBannerDomain}>{domainText}</Strong> to test recipients only.
      </Text>

      <Button variant="primary" size="sm" href={goLiveHref} className={styles.sandboxModeBannerCta}>
        Add your sending domain
      </Button>
    </Box>
  );
}
